import { Clock, GitBranch, Hourglass, Play, Variable, Zap } from 'lucide-react';
import { type DragEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn, generateNodeId } from '@/lib/utils';
import { useFlowStore } from '@/store/flow-store';

export interface NodeTypeConfig {
  type: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  defaultData: Record<string, unknown>;
}

export const nodeTypes = [
  {
    type: 'trigger',
    labelKey: 'nodes:types.trigger',
    icon: Zap,
    color: 'bg-amber-100 border-amber-400 text-amber-700 hover:bg-amber-200',
    defaultData: {
      trigger: 'state',
      entity_id: '',
    },
  },
  {
    type: 'condition',
    labelKey: 'nodes:types.condition',
    icon: GitBranch,
    color: 'bg-blue-100 border-blue-400 text-blue-700 hover:bg-blue-200',
    defaultData: {
      condition: 'state',
      entity_id: '',
    },
  },
  {
    type: 'action',
    labelKey: 'nodes:types.action',
    icon: Play,
    color: 'bg-green-100 border-green-400 text-green-700 hover:bg-green-200',
    defaultData: {
      service: 'light.turn_on',
    },
  },
  {
    type: 'delay',
    labelKey: 'nodes:types.delay',
    icon: Clock,
    color: 'bg-purple-100 border-purple-400 text-purple-700 hover:bg-purple-200',
    defaultData: {
      delay: '00:00:05',
    },
  },
  {
    type: 'wait',
    labelKey: 'nodes:types.wait',
    icon: Hourglass,
    color: 'bg-orange-100 border-orange-400 text-orange-700 hover:bg-orange-200',
    defaultData: {
      wait_template: '',
      timeout: '00:01:00',
    },
  },
  {
    type: 'set_variables',
    labelKey: 'nodes:types.set_variables',
    icon: Variable,
    color: 'bg-cyan-100 border-cyan-400 text-cyan-700 hover:bg-cyan-200',
    defaultData: {
      variables: {},
    },
  },
] as const satisfies readonly NodeTypeConfig[];

interface NodePaletteProps {
  iconOnly?: boolean;
}

export function NodePalette({ iconOnly = false }: NodePaletteProps) {
  const { t } = useTranslation(['common', 'nodes']);
  const addNode = useFlowStore((s) => s.addNode);
  const nodes = useFlowStore((s) => s.nodes);

  const handleAddNode = useCallback(
    (config: NodeTypeConfig) => {
      // Calculate position based on existing nodes - horizontal layout
      const baseX = nodes.length * 250 + 250;
      const baseY = 150;

      addNode({
        id: generateNodeId(config.type),
        type: config.type,
        position: { x: baseX, y: baseY },
        data: { ...config.defaultData },
      });
    },
    [addNode, nodes.length]
  );

  const onDragStart = useCallback((event: DragEvent<HTMLButtonElement>, config: NodeTypeConfig) => {
    // Store the node type data in the drag event
    event.dataTransfer.setData(
      'application/reactflow',
      JSON.stringify({
        type: config.type,
        defaultData: config.defaultData,
      })
    );
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  return (
    <div className={cn('space-y-2 p-4', iconOnly && 'p-2')}>
      <div className={cn('space-y-2', iconOnly && 'flex flex-col items-center')}>
        {nodeTypes.map((config) => (
          <Button
            key={config.type}
            variant="outline"
            onClick={() => handleAddNode(config)}
            onDragStart={(e) => onDragStart(e, config)}
            draggable
            className={cn(
              iconOnly ? 'h-12 w-12 justify-center p-0' : 'h-auto w-full justify-start gap-3 py-3',
              'cursor-grab transition-colors active:cursor-grabbing',
              config.color
            )}
          >
            <config.icon className="h-4 w-4" />
            {!iconOnly && <span className="font-medium text-sm">{t(config.labelKey)}</span>}
          </Button>
        ))}
      </div>
    </div>
  );
}
